import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Container, Table } from 'reactstrap';
import classNames from 'classnames/bind';
import moment from 'moment';

import AppMeta from 'components/App/Meta';
import AppBreadcrumb from 'components/App/Breadcrumb';
import Loading from 'components/App/Loading';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import { LOCATIONS } from 'constants/index';
import images from 'assets/images';
import { ReactComponent as PercentIcon } from 'assets/images/percent.svg';
import vatSettingApi from 'api/vatSettingApi';

import ModalAddEditVatService from './ModalAddEditVatService';
import styles from './VatService.module.scss';

const cx = classNames.bind(styles);

const formatEffectiveDate = (startAt, fallbackDate = '') => {
  if (startAt) {
    return moment(startAt).isValid() ? moment(startAt).format('D/M/YYYY') : startAt;
  }
  return fallbackDate || '';
};

const formatOtherFeeRateText = (fee) => {
  const feeValue = fee.value ?? fee.rate ?? '';
  const isPercent = fee.valueType === 'PERCENT' || fee.calcType === 'percent';

  let unitText = 'mỗi khách/đêm';
  const basis = fee.chargeBasis || fee.unitType || fee.unit;
  if (basis) {
    switch (basis) {
      case 'PER_NIGHT':
      case 'mỗi phòng/đêm':
        unitText = 'mỗi phòng/đêm';
        break;
      case 'PER_PERSON_PER_NIGHT':
      case 'mỗi khách/đêm':
        unitText = 'mỗi khách/đêm';
        break;
      case 'PER_PERSON_PER_STAY':
      case 'mỗi khách/lần lưu trú':
        unitText = 'mỗi khách/lần lưu trú';
        break;
      case 'PER_STAY':
      case 'mỗi phòng/lần lưu trú':
        unitText = 'mỗi phòng/lần lưu trú';
        break;
      default:
        if (typeof basis === 'string' && !['PERCENT', 'AMOUNT', '%', 'VND'].includes(basis)) {
          unitText = basis;
        }
        break;
    }
  }

  const feeNameLower = fee.name ? fee.name.toLowerCase() : 'phí';

  let formattedVal = feeValue;
  if (typeof feeValue === 'number' && !isPercent) {
    formattedVal = feeValue.toLocaleString('vi-VN');
  } else if (typeof feeValue === 'string' && !isPercent && feeValue) {
    const num = Number(feeValue.replace(/\./g, ''));
    if (!Number.isNaN(num) && num > 0) {
      formattedVal = num.toLocaleString('vi-VN');
    }
  }

  const valText = isPercent ? `${feeValue}%` : `${formattedVal} VND`;
  return `Thu tại khách sạn ${feeNameLower} ${valText} ${unitText}`;
};

function VatService(props) {
  const {
    isLoadingGetPlaceDetail,
    isLoadingUpdatePlaceServiceFee,
    isLoadingAddPlaceOtherFee,
    isLoadingUpdatePlaceOtherFee,
    isLoadingDeletePlaceOtherFee,
    placeDetail,
    actionGetPlaceDetail,
    actionClearPlaceDetail,
    actionUpdatePlaceServiceFee,
    actionAddPlaceOtherFee,
    actionUpdatePlaceOtherFee,
    actionDeletePlaceOtherFee,
  } = props;

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const [vatList, setVatList] = useState([]);
  const [appliedVatList, setAppliedVatList] = useState([]);
  const [isLoadingAppliedVat, setIsLoadingAppliedVat] = useState(false);
  const [isOpenAddEditModal, setIsOpenAddEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchAppliedVat = async () => {
      setIsLoadingAppliedVat(true);
      try {
        const response = await vatSettingApi.getAppliedVatSettings();
        const resData = response?.data || response;
        const list = [
          resData?.payload,
          resData?.data,
          resData,
        ].find(Array.isArray) || [];
        if (isMounted) {
          setAppliedVatList(list);
        }
      } catch (error) {
        // Ignore error
      } finally {
        if (isMounted) {
          setIsLoadingAppliedVat(false);
        }
      }
    };

    fetchAppliedVat();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (placeId) {
      actionGetPlaceDetail({ placeId });
    }
    return () => {
      if (actionClearPlaceDetail) {
        actionClearPlaceDetail();
      }
    };
  }, [actionClearPlaceDetail, actionGetPlaceDetail, placeId]);

  const renderStatusBadge = useCallback((status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'APPLIED') {
      return <span className={cx('StatusApplied')}>Đang áp dụng</span>;
    }
    if (s === 'UPCOMING' || s === 'PENDING') {
      return <span className={cx('StatusPending')}>Chưa diễn ra</span>;
    }
    if (s === 'STOPPED' || s === 'DISABLED') {
      return <span className={cx('StatusPending')}>Ngưng áp dụng</span>;
    }
    return <span className={cx('StatusPending')}>{status}</span>;
  }, []);

  useEffect(() => {
    const items = [];

    if (appliedVatList && appliedVatList.length > 0) {
      appliedVatList.forEach((vatItem, index) => {
        const startAt = vatItem.startAt || vatItem.effectiveDate;
        const formattedDate = startAt ? formatEffectiveDate(startAt) : '';
        const itemStatus = vatItem.status || (vatItem.isApplied ? 'APPLIED' : 'STOPPED');

        items.push({
          id: vatItem._id || vatItem.id || `appliedVat_${index}`,
          name: vatItem.name || 'Thuế GTGT',
          rateInfo: `Đã bao gồm thuế GTGT ${vatItem.value ?? 0}% trên giá nett`,
          effectiveDate: formattedDate,
          status: itemStatus,
          canEdit: false,
          canDelete: false,
          value: vatItem.value,
          isApplied: String(itemStatus).toUpperCase() === 'APPLIED',
        });
      });
    } else if (placeDetail?.vatFee || placeDetail?.vat) {
      const vatData = placeDetail.vatFee || placeDetail.vat;
      const vatStatus = vatData.status || (vatData.isApplied ? 'APPLIED' : 'STOPPED');
      items.push({
        id: 'vatFee',
        name: vatData.name || 'Thuế GTGT',
        rateInfo:
          vatData.rateInfo ||
          `Đã bao gồm thuế GTGT ${vatData.value ?? 0}% trên giá nett`,
        effectiveDate: vatData.effectiveDate || '',
        status: vatStatus,
        canEdit: false,
        canDelete: false,
        value: vatData.value,
        isApplied: String(vatStatus).toUpperCase() === 'APPLIED',
      });
    }

    if (placeDetail?.serviceFee) {
      const { value, isApplied, effectiveDate, startAt, rateInfo, status } = placeDetail.serviceFee;
      const formattedEffectiveDate = formatEffectiveDate(startAt, effectiveDate);
      const serviceStatus = status || (isApplied ? 'APPLIED' : 'STOPPED');

      items.push({
        id: 'serviceFee',
        name: 'Phí dịch vụ',
        rateInfo:
          rateInfo ||
          `Đã bao gồm Phí dịch vụ khách sạn ${value ?? 0}% tính trên giá nett`,
        effectiveDate: formattedEffectiveDate,
        status: serviceStatus,
        canEdit: true,
        canDelete: false,
        value,
        isApplied: String(serviceStatus).toUpperCase() === 'APPLIED',
        startAt,
      });
    }

    if (Array.isArray(placeDetail?.otherFees)) {
      placeDetail.otherFees.forEach((fee, index) => {
        const feeEffectiveDate = formatEffectiveDate(fee.startAt, fee.effectiveDate);
        const rateInfoText = formatOtherFeeRateText(fee);
        const feeStatus = fee.status || (fee.isApplied ? 'APPLIED' : 'STOPPED');

        items.push({
          id: fee._id || fee.id || `otherFee_${index}`,
          name: fee.name || 'Phí/Thuế khác',
          rateInfo: rateInfoText,
          effectiveDate: feeEffectiveDate,
          status: feeStatus,
          canEdit: true,
          canDelete: true,
          raw: fee,
        });
      });
    }

    setVatList(items);
  }, [appliedVatList, placeDetail]);

  const breadcrumbList = [
    {
      name: 'Chỗ nghỉ',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId || ''}`,
    },
    {
      name: 'Thuế & Phí',
      isActive: true,
      link: '',
    },
  ];

  const handleOpenAddModal = useCallback(() => {
    setEditingItem(null);
    setIsOpenAddEditModal(true);
  }, []);

  const handleOpenEditModal = useCallback((item) => {
    setEditingItem(item);
    setIsOpenAddEditModal(true);
  }, []);

  const handleToggleAddEditModal = useCallback(() => {
    setIsOpenAddEditModal((prev) => !prev);
  }, []);

  const handleSubmitVatItem = useCallback(
    (submittedItem) => {
      let startAtDate = moment().format('YYYY-MM-DD');
      if (submittedItem.effectiveDate && submittedItem.effectiveDate !== 'Hôm nay') {
        const parsed = moment(submittedItem.effectiveDate, 'DD/MM/YYYY');
        if (parsed.isValid()) {
          startAtDate = parsed.format('YYYY-MM-DD');
        } else {
          const fallbackParsed = moment(submittedItem.effectiveDate);
          if (fallbackParsed.isValid()) {
            startAtDate = fallbackParsed.format('YYYY-MM-DD');
          }
        }
      }

      if (submittedItem.id === 'serviceFee' || submittedItem.name === 'Phí dịch vụ') {
        actionUpdatePlaceServiceFee({
          placeId,
          value: Number(submittedItem.value ?? 0),
          startAt: startAtDate,
          onCallBackSave: () => actionGetPlaceDetail({ placeId }),
        });
        return;
      }

      const mapUnitTypeToChargeBasis = (unit) => {
        switch (unit) {
          case 'mỗi phòng/đêm':
            return 'PER_NIGHT';
          case 'mỗi khách/đêm':
            return 'PER_PERSON_PER_NIGHT';
          case 'mỗi khách/lần lưu trú':
            return 'PER_PERSON_PER_STAY';
          case 'mỗi phòng/lần lưu trú':
            return 'PER_STAY';
          default:
            return 'PER_PERSON_PER_NIGHT';
        }
      };

      const isPercent = submittedItem.calcType === 'percent';
      const valueType = isPercent ? 'PERCENT' : 'AMOUNT';
      const rawNum = String(submittedItem.amountValue ?? 0).replace(/\D/g, '');
      const value = isPercent
        ? Number(submittedItem.rate ?? 0)
        : Number(rawNum || 0);

      const otherFeeId = editingItem?.id || editingItem?.raw?._id || editingItem?.raw?.id;

      if (otherFeeId) {
        actionUpdatePlaceOtherFee({
          placeId,
          otherFeeId,
          name: submittedItem.name || 'Thuế/phí khác',
          chargeBasis: mapUnitTypeToChargeBasis(submittedItem.unitType),
          valueType,
          value,
          startAt: startAtDate,
          onCallBackSave: () => actionGetPlaceDetail({ placeId }),
        });
      } else {
        actionAddPlaceOtherFee({
          placeId,
          name: submittedItem.name || 'Thuế/phí khác',
          chargeBasis: mapUnitTypeToChargeBasis(submittedItem.unitType),
          valueType,
          value,
          startAt: startAtDate,
          onCallBackSave: () => actionGetPlaceDetail({ placeId }),
        });
      }
    },
    [
      actionAddPlaceOtherFee,
      actionGetPlaceDetail,
      actionUpdatePlaceOtherFee,
      actionUpdatePlaceServiceFee,
      editingItem,
      placeId,
    ],
  );

  const handleOpenDeleteModal = useCallback((item) => {
    setDeletingItem(item);
    setIsOpenDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeletingItem(null);
    setIsOpenDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deletingItem && deletingItem.id) {
      const otherFeeId = deletingItem.id || deletingItem.raw?._id || deletingItem.raw?.id;
      actionDeletePlaceOtherFee({
        placeId,
        otherFeeId,
        onCallBackSave: () => actionGetPlaceDetail({ placeId }),
      });
    }
    handleCloseDeleteModal();
  }, [
    actionDeletePlaceOtherFee,
    actionGetPlaceDetail,
    deletingItem,
    handleCloseDeleteModal,
    placeId,
  ]);

  return (
    <div className={`page-content ${cx('PageContent')}`}>
      <AppMeta name="THUẾ & PHÍ" />
      {(isLoadingGetPlaceDetail ||
        isLoadingAppliedVat ||
        isLoadingUpdatePlaceServiceFee ||
        isLoadingAddPlaceOtherFee ||
        isLoadingUpdatePlaceOtherFee ||
        isLoadingDeletePlaceOtherFee) && (
        <Loading loading loaderOnPage />
      )}
      <Container fluid>
        <AppBreadcrumb
          pageTitle="THUẾ & PHÍ"
          breadcrumbList={breadcrumbList}
        />

        {/* Top Banner Card */}
        <div className={cx('HeaderCard')}>
          <div className={cx('HeaderLeft')}>
            <div className={cx('IconWrapper')}>
              <PercentIcon />
            </div>
            <div className={cx('TextContainer')}>
              <h4 className={cx('Title')}>Thuế & phí</h4>
              <p className={cx('Description')}>
                Các thiết lập tại đây ảnh hưởng đến thuế & phí đã bao gồm/
                không bao gồm trong giá phòng mà khách hàng có thể nhìn thấy được
              </p>
            </div>
          </div>
          <div className={cx('HeaderRight')}>
            <button
              type="button"
              className={cx('AddButton')}
              onClick={handleOpenAddModal}
            >
              <span className={cx('PlusIcon')}>+</span>
              <span>Thêm thuế hoặc phí</span>
            </button>
          </div>
        </div>

        {/* Data Table Card */}
        <div className={cx('TableCard')}>
          <Table responsive className={cx('VatTable')}>
            <thead>
              <tr>
                <th className={cx('ColName')} aria-label="Tên thuế/phí" />
                <th className={cx('ColRate')}>Mức thu</th>
                <th className={cx('ColDate')}>Hiệu lực từ</th>
                <th className={cx('ColStatus')}>Trạng thái</th>
                <th className={cx('ColActions')}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {vatList.length > 0 ? (
                vatList.map((item) => (
                  <tr key={item.id}>
                    <td className={cx('ColName')}>{item.name}</td>
                    <td className={cx('ColRate')}>{item.rateInfo}</td>
                    <td className={cx('ColDate')}>{item.effectiveDate}</td>
                    <td className={cx('ColStatus')}>
                      {renderStatusBadge(item.status)}
                    </td>
                    <td className={cx('ColActions')}>
                      <div className={cx('ActionGroup')}>
                        {item.canEdit && (
                          <button
                            type="button"
                            className={cx('BtnEdit')}
                            onClick={() => handleOpenEditModal(item)}
                          >
                            Sửa
                          </button>
                        )}

                        {item.canDelete && item.id !== 'serviceFee' && item.name !== 'Phí dịch vụ' && (
                          <button
                            type="button"
                            className={cx('BtnDelete')}
                            onClick={() => handleOpenDeleteModal(item)}
                          >
                            Xoá
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    Chưa có dữ liệu thuế hoặc phí
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Container>

      {/* Add / Edit Modal */}
      <ModalAddEditVatService
        isOpen={isOpenAddEditModal}
        onToggle={handleToggleAddEditModal}
        onSubmit={handleSubmitVatItem}
        itemData={editingItem}
      />

      {/* Delete Confirmation Modal */}
      <ModalRequestAction
        isOpen={isOpenDeleteModal}
        icon={images.modalGarbageCan?.default || images.modalGarbageCan}
        textRequest="Bạn có chắc chắn muốn xoá thuế/phí này không?"
        onCloseModal={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

VatService.propTypes = {
  isLoadingGetPlaceDetail: PropTypes.bool,
  isLoadingUpdatePlaceServiceFee: PropTypes.bool,
  isLoadingAddPlaceOtherFee: PropTypes.bool,
  isLoadingUpdatePlaceOtherFee: PropTypes.bool,
  isLoadingDeletePlaceOtherFee: PropTypes.bool,
  placeDetail: PropTypes.instanceOf(Object),
  actionGetPlaceDetail: PropTypes.func,
  actionClearPlaceDetail: PropTypes.func,
  actionUpdatePlaceServiceFee: PropTypes.func,
  actionAddPlaceOtherFee: PropTypes.func,
  actionUpdatePlaceOtherFee: PropTypes.func,
  actionDeletePlaceOtherFee: PropTypes.func,
};

VatService.defaultProps = {
  isLoadingGetPlaceDetail: false,
  isLoadingUpdatePlaceServiceFee: false,
  isLoadingAddPlaceOtherFee: false,
  isLoadingUpdatePlaceOtherFee: false,
  isLoadingDeletePlaceOtherFee: false,
  placeDetail: null,
  actionGetPlaceDetail: () => {},
  actionClearPlaceDetail: () => {},
  actionUpdatePlaceServiceFee: () => {},
  actionAddPlaceOtherFee: () => {},
  actionUpdatePlaceOtherFee: () => {},
  actionDeletePlaceOtherFee: () => {},
};

export default VatService;
